/**
 * StepContent: step number, title, copy, button and icon inside a step card.
 * Single Responsibility: layout and copy; styling via constants.
 */

import React from 'react';
import PropTypes from 'prop-types';
import StepButton from './StepButton';

const CONTAINER_CLASS = 'min-h-[280px] sm:min-h-[300px] lg:min-h-[340px] relative w-full min-w-0 overflow-hidden';
const INNER_CLASS = 'absolute inset-0 pb-6 lg:pb-8 content-stretch flex flex-col justify-between items-start w-full h-full min-w-0 min-h-0';
const TITLE_SECTION_CLASS = 'content-stretch flex flex-col gap-3 sm:gap-4 items-start relative shrink-0 w-full max-w-full';
const STEP_NUMBER_ENABLED = 'text-black';
const STEP_NUMBER_DISABLED = 'text-[#d9d9d9]';
const STEP_NUMBER_CLASS_BASE = 'font-[\'Roboto:Semibold\',sans-serif] font-semibold leading-[normal] relative shrink-0 text-xl sm:text-2xl tracking-[0.12px] w-full';
const COPY_SECTION_ENABLED = 'text-black';
const COPY_SECTION_DISABLED = 'text-[#b9b9b9]';
const COPY_SECTION_CLASS_BASE = 'content-stretch flex flex-col gap-[12px] items-start leading-[normal] relative shrink-0 w-full';
const BOTTOM_SECTION_CLASS = 'content-stretch flex gap-2 sm:gap-[14px] items-end relative shrink-0 w-full min-w-0 pt-2';
const FONT_STYLE = { fontVariationSettings: "'wdth' 100" };

const StepContent = ({
  stepNumber,
  title,
  copy,
  isEnabled = false,
  buttonLabel,
  iconComponent,
  stats = {},
  getDynamicLabel,
}) => {
  const stepNumberClassName = `${STEP_NUMBER_CLASS_BASE} ${isEnabled ? STEP_NUMBER_ENABLED : STEP_NUMBER_DISABLED}`;
  const copySectionClassName = `${COPY_SECTION_CLASS_BASE} ${isEnabled ? COPY_SECTION_ENABLED : COPY_SECTION_DISABLED}`;

  return (
    <div className={CONTAINER_CLASS}>
      <div className={INNER_CLASS}>
        <div className={TITLE_SECTION_CLASS}>
          <p className={stepNumberClassName} style={FONT_STYLE}>
            {stepNumber}.
          </p>
          <div className={copySectionClassName}>
            <p
              className="font-['Roboto:Semibold',sans-serif] font-semibold relative shrink-0 text-lg sm:text-[22px] leading-[1.3] tracking-[0.11px] w-full"
              style={FONT_STYLE}
            >
              {title}
            </p>
            <div
              className="font-['Roboto:Regular',sans-serif] font-normal relative shrink-0 text-[15px] tracking-[0.075px] w-full"
              style={FONT_STYLE}
            >
              {typeof copy === 'string' ? <p className="mb-0">{copy}</p> : copy}
            </div>
          </div>
        </div>

        <div className={BOTTOM_SECTION_CLASS}>
          <StepButton
            label={buttonLabel}
            isEnabled={isEnabled}
            stats={stats}
            getDynamicLabel={getDynamicLabel}
          />
          <div
            className="flex shrink min-w-12 min-h-12 max-w-[45%] items-center justify-center overflow-hidden rounded-[4px] w-24 h-24 sm:w-28 sm:h-28 lg:w-[163px] lg:h-[163px]"
            data-name="Icon frame"
          >
            {iconComponent}
          </div>
        </div>
      </div>
    </div>
  );
};

StepContent.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  copy: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  isEnabled: PropTypes.bool,
  buttonLabel: PropTypes.string,
  iconComponent: PropTypes.node,
  stats: PropTypes.object,
  getDynamicLabel: PropTypes.func,
};

export default StepContent;
